import * as React from 'react';

import * as Sentry from '@sentry/react';

import { stripeTaxIdCodeMap } from 'js/constants/stripeTaxIdTypes';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Checkbox } from '@coursera/coursera-ui';

import type { Invoice } from 'bundles/internal-enterprise/components/lead/EnterpriseLeadContainer';
import { EnterpriseLeadContext } from 'bundles/internal-enterprise/components/lead/EnterpriseLeadContainer';
import EnterpriseLeadInvoiceSummary from 'bundles/internal-enterprise/components/lead/EnterpriseLeadSummary';
import { createInvoice } from 'bundles/internal-enterprise/components/lead/utils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import Modal from 'bundles/internal-ui/components/modal/Modal';

import 'css!./__styles__/EnterpriseLeadInvoicePreview';

type PropsFromCaller = {
  params: {
    leadId: string;
  };
};

type PropsFromStore = {
  invoice?: Invoice;
};

type Props = PropsFromCaller & PropsFromStore;

const EnterpriseLeadInvoicePreview: React.FC<Props> = (props) => {
  const { params } = props;
  const router = useRouter();

  const { invoice, resetInvoice, purchaseUserId } = React.useContext(EnterpriseLeadContext);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = React.useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
  const [errorModalRequestId, setErrorModalRequestId] = React.useState('');
  const [errorModalMessage, setErrorModalMessage] = React.useState('');
  const [useSandbox, setUseSandbox] = React.useState(false);

  React.useEffect(() => {
    // Redirect back to lead search if store is not available (e.g. after refresh)
    if (!invoice || invoice.leadId !== params.leadId) {
      router.push('/internal/enterprise/lead');
    }
  }, [invoice, params.leadId, router]);

  const handleSuccess = () => {
    setIsSuccessModalOpen(true);
    setIsConfirmModalOpen(false);
  };

  const handleError = (message: string, requestId: string) => {
    setIsErrorModalOpen(true);
    setErrorModalRequestId(requestId);
    setErrorModalMessage(message);
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);

    // clear store
    resetInvoice();
    router.push('/internal/enterprise/lead');
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleBack = () => {
    router.push(`/internal/enterprise/lead/invoice/${params.leadId}`);
  };

  const handleUseSandboxChange = () => {
    setUseSandbox((prevState) => !prevState);
  };

  const handleConfirmModalClose = () => {
    setIsConfirmModalOpen(false);
  };

  const handleConfirmModalOpen = () => {
    setIsConfirmModalOpen(true);
  };

  const handleSendInvoice = async () => {
    if (!invoice) {
      return;
    }

    if (!purchaseUserId) {
      handleError('purchaseUserId not set', 'client-validation');
      return;
    }

    handleConfirmModalClose();

    try {
      await createInvoice(invoice, useSandbox);

      handleSuccess();
    } catch (error) {
      // Extract error message - handle both REST API and GraphQL error formats
      // REST API: error.responseJSON.message
      // GraphQL: error.message
      const message = error?.responseJSON?.message || error?.message || 'An error occurred while creating the invoice';
      const requestId = error?.getResponseHeader?.('X-Coursera-Request-Id') || null;

      // Capture error to Sentry for monitoring with the exact API error message
      Sentry.captureException(error ?? 'Failed to create invoice order', {
        extra: {
          message,
          requestId,
        },
      });

      handleError(message, requestId);
    }
  };

  if (!invoice) {
    return null;
  }

  return (
    <div className="rc-EnterpriseLeadInvoicePreview">
      <Card>
        <h3>Preview invoice</h3>
        <div className="c-formSection">
          <div className="c-formSectionLabel">Owner</div>
          <div className="c-formSectionContent">{invoice.owner}</div>
        </div>

        <div className="c-formSection">
          <div className="c-formSectionLabel">Contract details</div>
          <div className="c-formSectionContent">
            <div className="c-summaryRow">
              <div className="c-label">Company name</div>
              <div className="c-value">{invoice.company}</div>
            </div>
            <div className="c-summaryRow">
              <div className="c-label">Company type</div>
              <div className="c-value">{invoice.organizationType}</div>
            </div>
            <div className="c-summaryRow m-b-1s color-danger">
              {invoice.existingOrg
                ? `* this will use an existing organization on the invoice - please ensure this is correct`
                : '* this will be used as the billing organization on the invoice - please ensure this is correct'}
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Customer email</div>
              <div className="c-value">{invoice.customerEmail}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">SFDC lead ID</div>
              <div className="c-value">{invoice.leadId}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Seats</div>
              <div className="c-value">{invoice.seats}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Billing type</div>
              <div className="c-value">{invoice.billingType}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Price</div>
              <div className="c-value">{invoice.priceType}</div>
            </div>
          </div>
        </div>

        <div className="c-formSection">
          <div className="c-formSectionLabel">Invoice details</div>
          <div className="c-formSectionDesc">
            These details will be added to the invoice-funded contract and recurring invoices, if set to auto-renew.
          </div>

          <div className="c-formSectinContent">
            <div className="c-summaryRow">
              <div className="c-label">First name</div>
              <div className="c-value">{invoice.firstName}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Last name</div>
              <div className="c-value">{invoice.lastName}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Role</div>
              <div className="c-value">{invoice.role}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Street Address </div>
              <div className="c-value">{invoice.address1}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Address, Line 2</div>
              <div className="c-value">{invoice.address2}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">City</div>
              <div className="c-value">{invoice.city}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">State/Province/Region</div>
              <div className="c-value">{invoice.state}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Postal/Zip Code</div>
              <div className="c-value">{invoice.postalCode}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Country</div>
              <div className="c-value">{invoice.country ? invoice.country.label : ''}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Tax ID Type</div>
              <div className="c-value">
                {invoice.taxIdTypeCode
                  ? // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                    `${invoice.taxIdTypeCode} (${stripeTaxIdCodeMap[invoice.taxIdTypeCode].name})`
                  : '(none)'}
              </div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">Tax/VAT ID</div>
              <div className="c-value">{invoice.taxId}</div>
            </div>

            <div className="c-summaryRow">
              <div className="c-label">PO Number</div>
              <div className="c-value">{invoice.poNumber}</div>
            </div>
          </div>
        </div>

        <EnterpriseLeadInvoiceSummary
          unitPrice={invoice.unitPrice}
          seats={invoice.seats}
          amountTotal={invoice.amountTotal}
          currencyCode={invoice.currencyCode}
        />

        {user.isSuperuserStrict() && (
          <div className="c-formSection c-buttonSection superuser">
            <Checkbox onChange={handleUseSandboxChange} checked={useSandbox}>
              Use Sandbox
            </Checkbox>
          </div>
        )}

        <div className="c-formSection c-buttonSection">
          <Button text="Back" btnStyle="default" onClick={handleBack} />
          <Button text="Send invoice to customer" btnStyle="info" onClick={handleConfirmModalOpen} />
        </div>

        <div className="c-formSection">
          Upon sending the invoice to the customer, the customer will receive paused access to their WES instance. They
          will receive full access after their first payment is settled.
        </div>
      </Card>

      <Modal
        title="Success!"
        onRequestClose={handleSuccessModalClose}
        open={isSuccessModalOpen}
        actions={[
          {
            text: 'Send another invoice',
            onClick: handleSuccessModalClose,
            style: 'info',
          },
        ]}
      >
        <div className="c-EnterpriseLeadInvoiceModal">
          <p className="c-headline">
            Invoice has been sent to <strong>{invoice.customerEmail}</strong> at <strong>{invoice.company}</strong>.
          </p>
          <p> Customer will get paused access to WES until the payment is settled.</p>
        </div>
      </Modal>

      <Modal
        title="Error"
        onRequestClose={handleErrorModalClose}
        open={isErrorModalOpen}
        actions={[
          {
            text: 'Try again',
            onClick: handleErrorModalClose,
            style: 'danger',
          },
        ]}
      >
        <div className="c-EnterpriseLeadInvoiceModal">
          <p className="c-headline">
            The invoice failed to send to the receipient. Please contact product/engineering for support.
          </p>
          <p>Error message: {errorModalMessage}</p>
          <p>
            <small>Request Id: {errorModalRequestId}</small>
          </p>
        </div>
      </Modal>

      <Modal
        title="Confirm"
        open={isConfirmModalOpen}
        onRequestClose={handleConfirmModalClose}
        actions={[
          {
            text: 'Send',
            onClick: handleSendInvoice,
            style: 'info',
          },
          {
            text: 'Cancel',
            onClick: handleConfirmModalClose,
            style: 'default',
          },
        ]}
      >
        <div className="c-EnterpriseLeadInvoiceModal">
          <p className="c-headline">Are you sure to send the invoice to customer?</p>
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseLeadInvoicePreview;
